/**
 * iter908 — Branch Price Management
 *
 * Two-column-per-branch price matrix so Admins / Managers can override
 * any menu item's price on a per-branch basis in one screen. Backed by
 * the `branch_menu_overrides` collection (iter907).
 *
 * UX:
 *   • Left column: menu item name + global (default) price for reference.
 *   • Next N columns: one per branch — each cell is a number input.
 *   • Blank / equal-to-global input = no override (deletes the row).
 *   • Non-blank & different-from-global = upserts an override row.
 *   • Only edited cells are pushed on "Save All" — one PATCH per delta.
 *
 * Access: `roles={['admin', 'manager']}` at the router level (App.js).
 * Design language matches BrandingSettingsPage / MenuItemsPage (Card +
 * DashboardLayout + shadcn table).
 */
import { useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Save, RefreshCw, Search, Undo2, DollarSign, Loader2, Building2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cur } from '@/lib/currency';

function toNumOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function BranchPriceManagementPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  // overrides: { [item_id]: { [branch_id]: { id, custom_price } } }
  const [overrides, setOverrides] = useState({});
  // edits: { [item_id]: { [branch_id]: string } }  ← raw input value strings
  const [edits, setEdits] = useState({});
  const [search, setSearch] = useState('');
  // iter914 — Bulk Copy Prices modal state.
  const [bulkCopyOpen, setBulkCopyOpen] = useState(false);
  const [bulkSrc, setBulkSrc] = useState('');
  const [bulkDst, setBulkDst] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      // Load menu WITHOUT branch_id so `price` is the true global.
      const [menuRes, brRes, ovRes] = await Promise.all([
        api.get('/cashier/menu-all'),
        api.get('/branches'),
        api.get('/cashier/menu-overrides'),
      ]);
      const menu = Array.isArray(menuRes.data) ? menuRes.data : (menuRes.data?.items || []);
      const brs = Array.isArray(brRes.data) ? brRes.data : (brRes.data?.items || []);
      setItems(menu.map((m) => ({
        id: m.id, name: m.name, name_ar: m.name_ar || '',
        category: m.category || '',
        price: Number(m.price ?? m.original_price ?? 0),
      })));
      setBranches(brs.map((b) => ({ id: b.id, name: b.name, currency: b.currency_code || 'SAR' })));
      // Index overrides
      const map = {};
      (Array.isArray(ovRes.data) ? ovRes.data : []).forEach((o) => {
        if (!o.item_id || !o.branch_id) return;
        if (!map[o.item_id]) map[o.item_id] = {};
        map[o.item_id][o.branch_id] = { id: o.id, custom_price: Number(o.custom_price) };
      });
      setOverrides(map);
      setEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Failed to load branch prices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) =>
      (it.name || '').toLowerCase().includes(q) ||
      (it.name_ar || '').toLowerCase().includes(q) ||
      (it.category || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const getCellValue = (itemId, branchId) => {
    // Edits take priority — even the empty string means "user typed blank"
    if (edits[itemId] && Object.prototype.hasOwnProperty.call(edits[itemId], branchId)) {
      return edits[itemId][branchId];
    }
    const ov = overrides[itemId]?.[branchId];
    return ov ? String(ov.custom_price) : '';
  };

  const setCell = (itemId, branchId, val) => {
    setEdits((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || {}), [branchId]: val },
    }));
  };

  const revertCell = (itemId, branchId) => {
    setEdits((prev) => {
      const next = { ...(prev[itemId] || {}) };
      delete next[branchId];
      const out = { ...prev, [itemId]: next };
      if (Object.keys(next).length === 0) delete out[itemId];
      return out;
    });
  };

  const dirtyCount = useMemo(() => {
    let n = 0;
    Object.keys(edits).forEach((iid) => {
      Object.keys(edits[iid] || {}).forEach(() => { n += 1; });
    });
    return n;
  }, [edits]);

  // iter914 — Bulk Copy Prices. Fills the edit buffer for every item in
  // the destination branch column with the source branch's current
  // price (its override if any, else the item's global price). Skips
  // items where the source has no override AND the destination already
  // matches the global — nothing to change. Nothing is persisted until
  // the user reviews and taps Save All.
  const applyBulkCopy = () => {
    if (!bulkSrc || !bulkDst || bulkSrc === bulkDst) return;
    let filled = 0;
    const nextEdits = { ...edits };
    items.forEach((it) => {
      const srcOv = overrides[it.id]?.[bulkSrc];
      const srcPrice = srcOv ? Number(srcOv.custom_price) : Number(it.price || 0);
      const dstOv = overrides[it.id]?.[bulkDst];
      const dstPrice = dstOv ? Number(dstOv.custom_price) : Number(it.price || 0);
      if (srcPrice === dstPrice) return; // nothing to change
      const row = { ...(nextEdits[it.id] || {}) };
      row[bulkDst] = String(srcPrice);
      nextEdits[it.id] = row;
      filled += 1;
    });
    setEdits(nextEdits);
    const srcName = branches.find((b) => b.id === bulkSrc)?.name || bulkSrc;
    const dstName = branches.find((b) => b.id === bulkDst)?.name || bulkDst;
    if (filled === 0) {
      toast.info(`${dstName} already matches ${srcName} — no changes needed`);
    } else {
      toast.success(`Copied ${filled} price${filled === 1 ? '' : 's'} from ${srcName} → ${dstName}. Review the amber cells, then press Save All to persist.`);
    }
    setBulkCopyOpen(false);
    setBulkSrc('');
    setBulkDst('');
  };

  const saveAll = async () => {
    if (dirtyCount === 0) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    const ops = { upserts: [], deletes: [] };
    Object.entries(edits).forEach(([itemId, byBr]) => {
      Object.entries(byBr).forEach(([branchId, raw]) => {
        const num = toNumOrNull(raw);
        const existing = overrides[itemId]?.[branchId];
        const globalPrice = items.find((it) => it.id === itemId)?.price ?? 0;
        // Empty input OR equal-to-global → delete override if it exists
        if (num === null || num === globalPrice) {
          if (existing?.id) ops.deletes.push({ item_id: itemId, branch_id: branchId, id: existing.id });
          return;
        }
        if (num < 0) return;   // silently ignore invalid negatives
        if (!existing || existing.custom_price !== num) {
          ops.upserts.push({ item_id: itemId, branch_id: branchId, custom_price: num });
        }
      });
    });

    let ok = 0;
    let failed = 0;
    // Deletes first so a "clear then re-set" (unlikely) doesn't collide
    for (const d of ops.deletes) {
      try { await api.delete(`/cashier/menu-overrides/${d.id}`); ok += 1; }
      catch { failed += 1; }
    }
    for (const u of ops.upserts) {
      try { await api.post('/cashier/menu-overrides', u); ok += 1; }
      catch { failed += 1; }
    }

    setSaving(false);
    if (failed === 0) {
      toast.success(`Saved ${ok} price change${ok === 1 ? '' : 's'}`);
    } else if (ok > 0) {
      toast.warning(`Saved ${ok}, ${failed} failed`);
    } else {
      toast.error(`Failed to save (${failed} error${failed === 1 ? '' : 's'})`);
    }
    await load();
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="branch-price-management">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 font-outfit">
              <DollarSign size={22} className="text-emerald-600" />
              Branch Price Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Set a custom price per branch for any menu item. Leave a cell blank (or set it equal to the default) to keep the global price. Changes only take effect when you press <b>Save All</b>.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={load} disabled={loading || saving} data-testid="bpm-reload">
              <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
              Reload
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setBulkCopyOpen(true)}
              disabled={loading || saving || branches.length < 2}
              data-testid="bpm-bulk-copy-open"
            >
              Copy Branch → Branch
            </Button>
            <Button
              size="sm"
              onClick={saveAll}
              disabled={saving || loading || dirtyCount === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="bpm-save-all"
            >
              {saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />}
              Save All{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
            </Button>
          </div>
        </div>

        {/* iter914 — Bulk Copy dialog: pre-fills every editable cell in
            the destination branch column with the source branch's
            current price (either override OR global if no override).
            User still has to press Save All to persist — gives them a
            chance to eyeball the diff first. */}
        {bulkCopyOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setBulkCopyOpen(false)} data-testid="bpm-bulk-copy-dialog">
            <div className="bg-background rounded-xl shadow-2xl p-5 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-bold">Copy Branch Prices</h2>
              <p className="text-xs text-muted-foreground">
                Fill the destination branch column with the source&apos;s current prices.
                Nothing is saved until you press <b>Save All</b>.
              </p>
              <div className="space-y-2 text-sm">
                <label className="block">
                  <span className="text-muted-foreground">Source branch</span>
                  <select
                    className="mt-1 w-full h-9 border rounded px-2 bg-background"
                    value={bulkSrc}
                    onChange={(e) => setBulkSrc(e.target.value)}
                    data-testid="bpm-bulk-copy-src"
                  >
                    <option value="">— Select —</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="text-muted-foreground">Destination branch</span>
                  <select
                    className="mt-1 w-full h-9 border rounded px-2 bg-background"
                    value={bulkDst}
                    onChange={(e) => setBulkDst(e.target.value)}
                    data-testid="bpm-bulk-copy-dst"
                  >
                    <option value="">— Select —</option>
                    {branches.filter(b => b.id !== bulkSrc).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setBulkCopyOpen(false)}>Cancel</Button>
                <Button
                  size="sm"
                  onClick={applyBulkCopy}
                  disabled={!bulkSrc || !bulkDst || bulkSrc === bulkDst}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="bpm-bulk-copy-apply"
                >
                  Copy into edit buffer
                </Button>
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Building2 size={16} />
                Price Matrix
                <Badge variant="outline" className="text-[10px]">
                  {items.length} items × {branches.length} branches
                </Badge>
              </CardTitle>
              <div className="flex-1" />
              <div className="relative w-full sm:w-64">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search items…"
                  className="pl-8 h-9 text-sm"
                  data-testid="bpm-search"
                />
              </div>
            </div>
            <CardDescription className="text-xs mt-2">
              Legend: <span className="text-muted-foreground">grey = uses global price</span> · <span className="text-emerald-700 font-semibold">green = active override</span> · <span className="text-amber-700 font-semibold">amber = pending change</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                <Loader2 className="animate-spin mx-auto mb-2" size={20} />
                Loading branches and menu items…
              </div>
            ) : branches.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No branches configured. Create a branch first from the Branches page.
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                {items.length === 0 ? 'No menu items yet — add items from the Menu Items page.' : 'No items match your search.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="bpm-price-table">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold w-64 sticky left-0 bg-muted/50 z-10">
                        Menu Item
                      </th>
                      <th className="text-right px-4 py-2 font-semibold w-28">
                        Default Price
                      </th>
                      {branches.map((br) => (
                        <th
                          key={br.id}
                          className="text-center px-3 py-2 font-semibold min-w-[140px] border-l"
                          data-testid={`bpm-th-${br.id}`}
                        >
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="truncate max-w-[140px]" title={br.name}>{br.name}</span>
                            <span className="text-[10px] text-muted-foreground font-normal">{br.currency}</span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it, rowIdx) => (
                      <tr
                        key={it.id}
                        className={rowIdx % 2 === 0 ? 'bg-background' : 'bg-muted/20'}
                        data-testid={`bpm-row-${it.id}`}
                      >
                        <td className="px-4 py-2 sticky left-0 bg-inherit z-[1]">
                          <div className="font-medium">{it.name}</div>
                          {it.name_ar && <div className="text-[11px] text-muted-foreground" dir="rtl">{it.name_ar}</div>}
                          {it.category && <Badge variant="outline" className="text-[9px] mt-0.5">{it.category}</Badge>}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                          {cur()} {it.price.toFixed(2)}
                        </td>
                        {branches.map((br) => {
                          const val = getCellValue(it.id, br.id);
                          const existing = overrides[it.id]?.[br.id];
                          const isDirty = !!(edits[it.id] && Object.prototype.hasOwnProperty.call(edits[it.id], br.id));
                          const numVal = toNumOrNull(val);
                          const isOverride = !isDirty && !!existing && existing.custom_price !== it.price;
                          const cellTone = isDirty ? 'ring-2 ring-amber-400 bg-amber-50 dark:bg-amber-950/30'
                            : isOverride ? 'ring-1 ring-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/20'
                              : '';
                          return (
                            <td
                              key={br.id}
                              className="px-2 py-1.5 border-l"
                              data-testid={`bpm-cell-${it.id}-${br.id}`}
                            >
                              <div className="flex items-center gap-1">
                                <Input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.01"
                                  min="0"
                                  value={val}
                                  onChange={(e) => setCell(it.id, br.id, e.target.value)}
                                  placeholder={it.price.toFixed(2)}
                                  className={`h-8 text-right tabular-nums text-xs ${cellTone}`}
                                  data-testid={`bpm-input-${it.id}-${br.id}`}
                                />
                                {isDirty && (
                                  <button
                                    type="button"
                                    onClick={() => revertCell(it.id, br.id)}
                                    title="Revert this change"
                                    className="text-muted-foreground hover:text-foreground shrink-0"
                                    data-testid={`bpm-revert-${it.id}-${br.id}`}
                                  >
                                    <Undo2 size={12} />
                                  </button>
                                )}
                              </div>
                              {!isDirty && numVal !== null && numVal !== it.price && (
                                <div className="text-[9px] text-emerald-700 text-right mt-0.5">
                                  {numVal > it.price ? `+${(numVal - it.price).toFixed(2)}` : (numVal - it.price).toFixed(2)}
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
